/**
 * RoleSwitcher - sidebar component.
 *
 * Single-role users: static badge.
 * Multi-role users: chevron + dropdown listing allowedRoles.
 *
 * Per CHAP-62 + `~/.claude/rules/role-pattern.md`. Ported from
 * 1116/course-app/src/client/components/layout/RoleSwitcher.tsx.
 */
import { useEffect, useRef, useState } from 'react';
import { useUserRoles } from '../../hooks/useUserRoles.js';
import {
  resolveActiveRole,
  setStoredRole,
  roleLabel,
  roleAccent,
} from '../../lib/active-role.js';
import type { Role } from '../../../shared/types.js';

const ROLE_ORDER: Role[] = ['admin', 'team', 'customer'];

interface Props {
  /** Current URL path. Caller passes window.location.pathname or React Router location. */
  pathname: string;
  /** Optional override for the active role (e.g. from a context provider). */
  forcedActiveRole?: Role;
  /** Callback when the user picks a new role. Caller navigates + may setStoredRole. */
  onRoleChange?: (role: Role) => void;
}

export function RoleSwitcher({ pathname, forcedActiveRole, onRoleChange }: Props) {
  const { primaryRole, allowedRoles, tier, isMultiRole, isLoading } = useUserRoles();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  const active = forcedActiveRole ?? resolveActiveRole({ url: pathname, primaryRole, allowedRoles });

  // Click-outside + Esc close
  useEffect(() => {
    if (!open) return;
    function onPointer(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    window.addEventListener('mousedown', onPointer);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onPointer);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (isLoading) {
    return (
      <div className="px-3 py-2 text-sm text-neutral animate-pulse" aria-busy="true">
        Loading…
      </div>
    );
  }

  const accent = roleAccent(active);
  const labelText = roleLabel(active, active === 'customer' ? tier : null);

  // Single-role: static badge
  if (!isMultiRole) {
    return (
      <div className="px-3 py-2 flex items-center gap-2 min-w-0" style={{ borderLeft: `3px solid ${accent}` }}>
        <span
          className="w-2 h-2 rounded-full flex-shrink-0"
          style={{ backgroundColor: accent }}
          aria-hidden
        />
        <span className="text-sm font-medium text-white truncate">{labelText}</span>
      </div>
    );
  }

  // Multi-role: button + dropdown
  return (
    <div ref={ref} className="relative min-w-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="w-full px-3 py-2 flex items-center justify-between gap-2 hover:bg-navy-light rounded-lg text-sm font-medium text-white min-w-0"
        style={{ borderLeft: `3px solid ${accent}` }}
      >
        <span className="flex items-center gap-2 min-w-0">
          <span
            className="w-2 h-2 rounded-full flex-shrink-0"
            style={{ backgroundColor: accent }}
            aria-hidden
          />
          <span className="truncate">{labelText}</span>
        </span>
        <svg
          className={`w-4 h-4 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <>
          {/* Mobile backdrop — closes the bottom sheet on click. Hidden from sm: up. */}
          <div
            className="fixed inset-0 z-40 bg-black/40 sm:hidden"
            aria-hidden="true"
            onClick={() => setOpen(false)}
          />
          <div
            role="menu"
            className="fixed inset-x-0 bottom-0 z-50 rounded-t-xl shadow-2xl bg-white border border-gray-200 overflow-hidden sm:absolute sm:inset-x-auto sm:bottom-auto sm:left-0 sm:right-0 sm:mt-1 sm:rounded-lg sm:shadow-lg"
          >
            {ROLE_ORDER.filter((r) => allowedRoles.includes(r)).map((r) => {
              const isActive = r === active;
              const itemAccent = roleAccent(r);
              return (
                <button
                  key={r}
                  role="menuitem"
                  onClick={() => {
                    if (!isActive) {
                      setStoredRole(r, allowedRoles);
                      onRoleChange?.(r);
                    }
                    setOpen(false);
                  }}
                  className="w-full px-3 py-2 flex items-center justify-between gap-2 text-sm hover:bg-gray-50 text-navy min-w-0"
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <span
                      className="w-2 h-2 rounded-full flex-shrink-0"
                      style={{ backgroundColor: itemAccent }}
                      aria-hidden
                    />
                    <span className="truncate">{roleLabel(r, r === 'customer' ? tier : null)}</span>
                  </span>
                  {isActive && (
                    <svg
                      className="w-4 h-4 flex-shrink-0"
                      style={{ color: itemAccent }}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
